import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ClinicianDashboard from './ClinicianDashboard';
import PatientProfile from './PatientProfile';
import '../styles/PatientProfilePage.css';

const PatientProfilePage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patients, updatePatient } = useAuth();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔍 PatientProfilePage useEffect - patientId:', patientId);
    console.log('🔍 Current patients data:', patients);
    
    // Handle test patient ID
    if (patientId === 'test-patient-123') {
      console.log('👋 Using test patient data');
      setPatient({
        id: 'test-patient-123',
        firstName: 'Test',
        lastName: 'Patient',
        email: 'test.patient@example.com',
        dateOfBirth: '1990-01-01',
        phone: '555-123-4567',
        address: '123 Test St, Testville',
        // Add any other required fields
      });
      setLoading(false);
      return;
    }
    
    // If we have patients data, try to find the patient
    if (patients) {
      console.log(`🔍 Searching ${patients.length} patients for ID:`, patientId);
      // Find the patient by ID
      const foundPatient = patients.find(p => p.id === patientId);
      if (foundPatient) {
        console.log('✅ Found patient:', { id: foundPatient.id, name: `${foundPatient.firstName} ${foundPatient.lastName}` });
        setPatient(foundPatient);
      } else if (patients.length > 0) {
        // We have patients but didn't find this one
        console.error('❌ Patient not found with ID:', patientId);
        console.log('Available patient IDs:', patients.map(p => p.id));
        setPatient(null); // Explicitly set patient to null to trigger not found UI
      }
      // Always set loading to false once we've processed the patients data
      setLoading(false);
    } else {
      // No patients data yet, but we'll keep loading state
      console.log('⏳ No patients data available yet');
      setLoading(true);
    }
  }, [patientId, patients]);

  const handleBackToPatients = () => {
    navigate('/clinician/patients');
  };

  const handleUpdatePatient = async (updatedPatient) => {
    console.log('handleUpdatePatient called with:', updatedPatient);
    try {
      if (updatePatient) {
        // Pass both patient ID and the updated data
        const result = await updatePatient(patientId, updatedPatient);
        console.log('updatePatient result:', result);
        
        if (result && result.success === false) {
          console.error('Failed to update patient:', result.error);
          return { success: false, error: result.error };
        }
        
        // Only update local state if the server update was successful
        setPatient(prev => ({
          ...prev,
          ...updatedPatient,
          // Ensure groups is always an array
          groups: updatedPatient.groups || []
        }));
        
        return { success: true };
      }
    } catch (error) {
      console.error('Error in handleUpdatePatient:', error);
      return { success: false, error: error.message };
    }
  };

  console.log('🔄 Rendering PatientProfilePage:', { 
    loading, 
    hasPatient: !!patient,
    patientId,
    patientsCount: patients?.length || 0
  });

  if (loading) {
    console.log('⏳ Showing loading state');
    return (
      <ClinicianDashboard>
        <div className="patient-profile-page">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading patient data for ID: {patientId}...</p>
          </div>
        </div>
      </ClinicianDashboard>
    );
  }

  if (!patient) {
    // If we have patients data but didn't find the patient, show not found
    if (patients && patients.length > 0) {
      return (
        <ClinicianDashboard>
          <div className="patient-profile-page">
            <div className="error-container">
              <h2>Patient not found</h2>
              <p>No patient found with ID: {patientId}</p>
              <button 
                onClick={handleBackToPatients}
                className="btn btn-primary"
              >
                Back to Patients
              </button>
            </div>
          </div>
        </ClinicianDashboard>
      );
    }
    
    // If we don't have patients data yet, keep showing loading
    return (
      <ClinicianDashboard>
        <div className="patient-profile-page">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading patient data...</p>
          </div>
        </div>
      </ClinicianDashboard>
    );
  }

  return (
    <ClinicianDashboard>
      <div className="patient-profile-page">
        <div className="header">
          {/* <button 
            onClick={handleBackToPatients}
            className="back-button"
          >
            ← Back to Patients
          </button> */}
        </div>
        
        <PatientProfile 
          patient={patient} 
          onUpdatePatient={handleUpdatePatient} 
          onBack={handleBackToPatients}
          isEditable={true}
        />
      </div>
    </ClinicianDashboard>
  );
};

export default PatientProfilePage;
